import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Primitive } from '../../../models/Primitive';
import { IrisFieldBaseComponent } from '../field-base/field-base.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StrakitButtonGroupComponent } from '@ngx-strakit/cdk/controls/button-group';
import { StrakitButtonComponent } from '@ngx-strakit/cdk/controls';
import { StrakitButtonPrefixDirective } from '@ngx-strakit/cdk';

export interface ButtonGroupSelectorOptionI<T = Primitive> {
  value: T;
  title: string;
  icon?: string;
  tooltip?: string;
}

export enum ButtonGroupView {
  Default = 'default',
  Tab = 'tab',
}

@Component({
  selector: 'iris-field-button-group-selector',
  templateUrl: './button-group-selector.component.html',
  styleUrls: ['./button-group-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IrisFieldButtonGroupSelectorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTooltipModule,
    StrakitButtonGroupComponent,
    StrakitButtonComponent,
    StrakitButtonPrefixDirective,
  ],
})
export class IrisFieldButtonGroupSelectorComponent extends IrisFieldBaseComponent implements ControlValueAccessor {

  readonly options = input<ButtonGroupSelectorOptionI[]>([]);
  readonly view = input<ButtonGroupView>(ButtonGroupView.Default);
  readonly multi = input(false);
  readonly suppressButtonsMinWidth = input(false);
  readonly hideTitle = input(false, { transform: booleanAttribute });
  readonly resetOnSecondClick = input(false, { transform: booleanAttribute });

  readonly disabled = signal(false);

  isSelected(item: ButtonGroupSelectorOptionI): boolean {
    if (this.multi()) {
      return Array.isArray(this.valueControl.value) && this.valueControl.value.includes(item.value);
    }
    return this.valueControl.value === item.value;
  }

  override writeValue(obj: Primitive | Primitive[] | null): void {
    super.writeValue(obj);
    // Required for OnPush so prefilled form values are reflected without user interaction.
    this.changeDetector.markForCheck();
  }

  onSelect(value: Primitive): void {
    if (this.disabled()) {
      return;
    }

    if (this.multi()) {
      let newVal: Primitive[] = [value];
      if (Array.isArray(this.valueControl.value)) {
        newVal = this.valueControl.value.includes(value) ?
          this.valueControl.value.filter(item => item !== value) :
          [...this.valueControl.value ?? [], value];
      }

      this.valueControl.setValue(newVal);
    } else {
      if (this.resetOnSecondClick() !== false && value === this.valueControl.value) {
        this.valueControl.reset(null);
      } else {
        this.valueControl.setValue(value);
      }
    }
  }

  override setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    this.changeDetector.markForCheck();
  }
}
